import { opensTurn } from '@littlebird/ui-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Message as TMessage } from '@littlebird/sdk';

/** The anchor sits at the viewport top, so most of the window goes below it. */
const WINDOW_MESSAGES_ABOVE_ANCHOR = 3;
/** Messages added per expansion when scrolling toward a window edge. */
export const WINDOW_EXPAND_COUNT = 20;

/**
 * The rendered slice of the in-memory thread. 'tail' follows the array end
 * so newly streamed messages stay visible.
 */
interface MessageWindow {
  start: number;
  end: number | 'tail';
}

/**
 * A logical chat turn starts at a user message and includes every following
 * row up to the next user message that opens a turn. A message the user sent
 * into a running turn stays inside it. The raw message budget is only a
 * sizing target: rendering must never begin or end inside one of these turns
 * because hidden assistant rows are grouped into one reasoning panel later in
 * the render pipeline.
 */
const findTurnStart = (messages: TMessage[], candidate: number): number => {
  let index = Math.max(0, Math.min(candidate, messages.length - 1));
  while (index > 0 && !opensTurn(messages[index])) index -= 1;
  return index;
};

/** Returns an exclusive end index at the next logical-turn boundary. */
const findTurnEnd = (messages: TMessage[], candidate: number): number => {
  let index = Math.max(0, Math.min(candidate, messages.length));
  while (index < messages.length && !opensTurn(messages[index])) {
    index += 1;
  }
  return index;
};

const findWindowStartAroundAnchor = (
  messages: TMessage[],
  anchorIndex: number
): number => {
  const anchorTurnStart = findTurnStart(messages, anchorIndex);
  const contextualStart = findTurnStart(
    messages,
    Math.max(anchorIndex - WINDOW_MESSAGES_ABOVE_ANCHOR, 0)
  );
  // Include nearby complete turns above the anchor, but do not pull a very
  // large previous turn into the window just to satisfy a small context target
  return anchorTurnStart - contextualStart <= WINDOW_MESSAGES_ABOVE_ANCHOR
    ? contextualStart
    : anchorTurnStart;
};

const toWindowEnd = (
  messages: TMessage[],
  candidate: number
): MessageWindow['end'] => {
  const end = findTurnEnd(messages, candidate);
  return end >= messages.length ? 'tail' : end;
};

/**
 * Renders a window of the thread instead of the full message list: fresh
 * opens window the tail, restores window the saved anchor message, and the
 * returned operations expand or re-center the window in place.
 */
export function useSplitThread(
  messages: Array<TMessage>,
  {
    count,
    onSplit,
    resetKey,
    anchorMessageId,
  }: {
    count: number;
    onSplit?: (splitMessages: TMessage[]) => void;
    resetKey: string;
    /** Center the initial window on this message instead of the tail. */
    anchorMessageId?: string | null;
  }
) {
  const onSplitRef = useRef(onSplit);
  onSplitRef.current = onSplit;

  const messagesLength = messages?.length ?? 0;

  // Derived until an expansion stamps an override, so late message
  // hydration keeps flowing into the initial window.
  const [windowOverride, setWindowOverride] = useState<
    (MessageWindow & { resetKey: string }) | null
  >(null);

  // A restored window that reaches the tail keeps following it. Otherwise a
  // turn appended at or past `start + count` lands beyond the turn-aligned
  // end and is never rendered.
  const restoreKey = `${resetKey}:${anchorMessageId ?? ''}`;
  const [tailFollowingRestoreKey, setTailFollowingRestoreKey] = useState<
    string | null
  >(null);
  if (tailFollowingRestoreKey && tailFollowingRestoreKey !== restoreKey) {
    setTailFollowingRestoreKey(null);
  }
  const isFollowingTail = tailFollowingRestoreKey === restoreKey;

  const hasOverride = windowOverride?.resetKey === resetKey;
  const anchorIndex = useMemo(
    () =>
      anchorMessageId
        ? messages.findIndex((message) => message.id === anchorMessageId)
        : -1,
    [anchorMessageId, messages]
  );
  const isAnchoredWindow =
    !hasOverride && messagesLength > count && anchorIndex !== -1;

  const messageWindow = useMemo<MessageWindow>(() => {
    if (windowOverride && windowOverride.resetKey === resetKey) {
      return windowOverride;
    }
    if (messagesLength <= count) return { start: 0, end: 'tail' };
    if (anchorIndex === -1) {
      return {
        start: findTurnStart(messages, Math.max(messagesLength - count, 0)),
        end: 'tail',
      };
    }
    const start = findWindowStartAroundAnchor(messages, anchorIndex);
    return {
      start,
      end: isFollowingTail ? 'tail' : toWindowEnd(messages, start + count),
    };
  }, [
    anchorIndex,
    count,
    isFollowingTail,
    messages,
    messagesLength,
    resetKey,
    windowOverride,
  ]);

  if (isAnchoredWindow && messageWindow.end === 'tail' && !isFollowingTail) {
    setTailFollowingRestoreKey(restoreKey);
  }

  const windowRef = useRef(messageWindow);
  windowRef.current = messageWindow;
  const resetKeyRef = useRef(resetKey);
  resetKeyRef.current = resetKey;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const splitState = useMemo(() => {
    const isFullyLoaded =
      messageWindow.start === 0 && messageWindow.end === 'tail';
    if (isFullyLoaded) {
      return { isFullyLoaded: true, thread: messages, threadStartIndex: 0 };
    }
    const end =
      messageWindow.end === 'tail'
        ? messagesLength
        : Math.min(messageWindow.end, messagesLength);
    return {
      isFullyLoaded: false,
      thread: messages.slice(messageWindow.start, end),
      threadStartIndex: messageWindow.start,
    };
  }, [messages, messagesLength, messageWindow]);

  // Fire once per thread: the messages array gets a new identity on every
  // streaming chunk, and re-firing stomps the user's scroll mid-stream.
  const onSplitFiredForKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!splitState.thread.length) return;
    if (onSplitFiredForKeyRef.current === resetKey) return;
    onSplitFiredForKeyRef.current = resetKey;
    onSplitRef.current?.(splitState.thread);
  }, [splitState.thread, resetKey]);

  /** Prepend more messages above the window. Callers wrap in shift mode. */
  const expandUp = useCallback(() => {
    const current = windowRef.current;
    if (current.start === 0) return false;
    const messages = messagesRef.current;
    const start = findTurnStart(
      messages,
      Math.max(current.start - WINDOW_EXPAND_COUNT, 0)
    );
    if (start === current.start) return false;
    setWindowOverride({
      start,
      end: current.end,
      resetKey: resetKeyRef.current,
    });
    return true;
  }, []);

  /** Append more messages below the window; appends don't move content. */
  const expandDown = useCallback(() => {
    const current = windowRef.current;
    if (current.end === 'tail') return false;
    const messages = messagesRef.current;
    const nextEnd = findTurnEnd(messages, current.end + WINDOW_EXPAND_COUNT);
    setWindowOverride({
      start: current.start,
      end: nextEnd >= messages.length ? 'tail' : nextEnd,
      resetKey: resetKeyRef.current,
    });
    return true;
  }, []);

  /** Reset to a fresh bounded tail window (sends, jump-to-latest). */
  const windowTail = useCallback(() => {
    const current = windowRef.current;
    const length = messagesRef.current.length;
    // Near-tail windows stay put so ordinary sends don't churn; anything
    // wider (expansions, a find's full load) re-bounds to the tail.
    if (current.end === 'tail' && length - current.start <= count * 2) {
      return false;
    }
    setWindowOverride({
      start: findTurnStart(messagesRef.current, Math.max(length - count, 0)),
      end: 'tail',
      resetKey: resetKeyRef.current,
    });
    return true;
  }, [count]);

  /** Widen the window to the tail keeping its start (the find reveal). */
  const extendToTail = useCallback(() => {
    const current = windowRef.current;
    if (current.end === 'tail') return false;
    setWindowOverride({
      start: current.start,
      end: 'tail',
      resetKey: resetKeyRef.current,
    });
    return true;
  }, []);

  /** Re-center the window on a message (fast-scroll jumps). */
  const windowAroundMessage = useCallback((messageId: string) => {
    const msgs = messagesRef.current;
    const anchorIndex = msgs.findIndex((message) => message.id === messageId);
    if (anchorIndex === -1) return false;
    const current = windowRef.current;
    const currentEnd = current.end === 'tail' ? msgs.length : current.end;
    if (anchorIndex >= current.start && anchorIndex < currentEnd) return true;
    const start = findWindowStartAroundAnchor(msgs, anchorIndex);
    const end = findTurnEnd(
      msgs,
      Math.max(start + WINDOW_EXPAND_COUNT, anchorIndex + 1)
    );
    setWindowOverride({
      start,
      end: end >= msgs.length ? 'tail' : end,
      resetKey: resetKeyRef.current,
    });
    return true;
  }, []);

  /** Render everything; find needs every item mounted. */
  const loadFully = useCallback(() => {
    const current = windowRef.current;
    if (current.start === 0 && current.end === 'tail') return false;
    setWindowOverride({ start: 0, end: 'tail', resetKey: resetKeyRef.current });
    return true;
  }, []);

  return {
    thread: splitState.thread,
    threadStartIndex: splitState.threadStartIndex,
    isFullyLoaded: splitState.isFullyLoaded,
    isTailWindow: messageWindow.end === 'tail',
    expandUp,
    expandDown,
    extendToTail,
    windowTail,
    windowAroundMessage,
    loadFully,
  };
}
